import { cityById } from "../model/cities.ts";
import { presetById } from "../model/defaults.ts";
import { BUCKETS } from "../model/schedule.ts";
import { compareTimings } from "../model/repatriation.ts";
import type { RepatriationInputs, RepatriationResult } from "../model/repatriation.ts";
import type { ExpensePlan, ExpenseSummary } from "../model/expenses.ts";
import { TIER_LABEL } from "../model/expenses.ts";
import type { BucketKey, PlanInputs, SolveResult } from "../model/types.ts";
import { crore, money, multiple, pct, usd } from "../lib/format.ts";
import { gapOf } from "../lib/gap.ts";
import type { OtherSavings } from "../lib/otherSavings.ts";
import { NPS_UNLOCK_AGE } from "../lib/otherSavings.ts";

const BUCKET_LABEL: Record<BucketKey, string> = {
  life: "Lifestyle",
  undergrad: "Undergraduate education",
  school: "Schooling",
  elder: "Parents' care",
  health: "Health cover",
  access: "Getting to services",
};

/**
 * Print-only view of all three tabs. Hidden on screen; shown instead of the app
 * by the print stylesheet, so "Print summary" (or Save as PDF) produces a clean
 * report whichever tab is open.
 */
export function PrintReport({
  inputs,
  result,
  retireYear,
  plan,
  expenses,
  repat,
  repatResult,
  repatIsExample,
  otherSavings,
  otherCounted,
  npsCounted,
  planInUse,
}: {
  inputs: PlanInputs;
  result: SolveResult | null;
  retireYear: number;
  plan: ExpensePlan;
  expenses: ExpenseSummary;
  repat: RepatriationInputs;
  repatResult: RepatriationResult;
  repatIsExample: boolean;
  otherSavings: OtherSavings;
  otherCounted: number;
  npsCounted: boolean;
  planInUse: boolean;
}) {
  const h = inputs.household;
  const s = inputs.spending;
  const city = cityById(s.cityId);
  const corpus = result?.corpus["0.90"] ?? null;
  const grossInr = repatResult.grossUsd * repat.fxRate;
  const gross = corpus !== null ? gapOf(corpus, grossInr + otherCounted) : null;
  const net = corpus !== null ? gapOf(corpus, repatResult.netInr + otherCounted) : null;
  const timings = compareTimings(repat);
  const printed = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="print-report">
      <header className="pr-head">
        <p className="pr-brand">Enoughwise · enoughwise.com</p>
        <h1>Retirement summary</h1>
        <p className="pr-meta">
          Printed {printed}. Estimates from the assumptions shown — not tax, legal or financial advice.
        </p>
      </header>

      <section>
        <h2>At a glance</h2>
        <table className="pr-table">
          <tbody>
            <tr><th>Corpus needed (90% confidence)</th><td>{corpus !== null ? crore(corpus, 2) : "Not yet calculated"}</td></tr>
            <tr><th>Gross US savings{repatIsExample ? " (example figures)" : ""}</th><td>{crore(grossInr, 2)} ({usd(repatResult.grossUsd)})</td></tr>
            <tr><th>Other savings, today's value before tax</th><td>{crore(otherCounted, 2)}</td></tr>
            {gross && <tr className="pr-strong"><th>{gross.label}, before tax</th><td>{crore(gross.amount, 2)}</td></tr>}
            <tr><th>US savings landing in India after tax and transfer costs</th><td>{crore(repatResult.netInr, 2)}</td></tr>
            {net && <tr className="pr-strong"><th>{net.label}, with US savings after tax</th><td>{crore(net.amount, 2)}</td></tr>}
            <tr><th>Spending plan{Object.keys(plan.overrides).length === 0 && plan.custom.length === 0 ? " (standard figures)" : ""}</th><td>{money(expenses.annualTotal)} a year</td></tr>
            <tr><th>Living costs the calculator uses</th><td>{money(inputs.spending.lifestylePerYear)} a year ({planInUse ? "from the spending plan" : "own amount, not the spending plan"})</td></tr>
          </tbody>
        </table>
        {repat.saleYear !== retireYear && (
          <p className="pr-note">US savings are valued in {repat.saleYear}; the corpus is needed in {retireYear}.</p>
        )}
      </section>

      <section>
        <h2>1. Corpus calculator</h2>
        <h3>Household and spending (today's rupees)</h3>
        <table className="pr-table">
          <tbody>
            <tr><th>Age at retirement</th><td>{h.adultAge}, retiring in {retireYear}</td></tr>
            <tr><th>Children</th><td>{h.childAges.length ? h.childAges.map((a) => `${a}`).join(", ") + " (ages at retirement)" : "None"}</td></tr>
            <tr><th>Dependent parents</th><td>{h.parentCount ? `${h.parentCount}, average age ${h.parentAge}` : "None"}</td></tr>
            <tr><th>Money must last</th><td>{inputs.horizonYears} years (to age {h.adultAge + inputs.horizonYears})</td></tr>
            <tr><th>City</th><td>{city.name}</td></tr>
            <tr><th>Lifestyle per year</th><td>{money(s.lifestylePerYear)}{s.lifestylePricedLocally ? ` (priced for ${city.name})` : " (Bengaluru prices, adjusted for the city)"}</td></tr>
            {h.childAges.length > 0 && <tr><th>Schooling per child per year</th><td>{money(s.schoolPerChildPerYear)}</td></tr>}
            {h.childAges.length > 0 && <tr><th>Undergraduate per child per year</th><td>{money(s.undergradPerYear)}</td></tr>}
            <tr><th>Health cover per year</th><td>{money(s.healthCoverPerYear)}</td></tr>
            <tr><th>Investments</th><td>{presetById(inputs.markets.presetId).label}</td></tr>
          </tbody>
        </table>

        {result && (
          <>
            <h3>Results</h3>
            <table className="pr-table">
              <tbody>
                {Object.entries(result.corpus)
                  .sort(([a], [b]) => +a - +b)
                  .map(([conf, v]) => (
                    <tr key={conf} className={conf === "0.90" ? "pr-strong" : undefined}>
                      <th>Corpus for {pct(+conf, 0)} confidence</th><td>{crore(v, 2)}</td>
                    </tr>
                  ))}
                <tr><th>Year-one outflow, including tax</th><td>{money(result.yearOneGross)}</td></tr>
                <tr><th>Implied withdrawal rate</th><td>{pct(result.impliedSwr, 2)}</td></tr>
                <tr><th>Corpus multiple</th><td>{multiple(result.multiple)}</td></tr>
              </tbody>
            </table>
            <h3>Where the money goes</h3>
            <table className="pr-table">
              <tbody>
                {[...BUCKETS]
                  .sort((a, b) => result.bucketCost[b] - result.bucketCost[a])
                  .filter((k) => result.bucketCost[k] > 0)
                  .map((k) => (
                    <tr key={k}><th>{BUCKET_LABEL[k]}</th><td>{crore(result.bucketCost[k], 2)}</td></tr>
                  ))}
                <tr><th>Buffer for an unknown first decade</th><td>+{crore((corpus ?? 0) - result.deterministic, 2)}</td></tr>
              </tbody>
            </table>
          </>
        )}
      </section>

      <section>
        <h2>2. Spending plan</h2>
        <p className="pr-note">
          {TIER_LABEL[plan.tier]} tier, {cityById(plan.cityId).name}; {plan.adults} adult{plan.adults === 1 ? "" : "s"},{" "}
          {plan.children} child{plan.children === 1 ? "" : "ren"}, {plan.parentsLiveIn} parent{plan.parentsLiveIn === 1 ? "" : "s"} living in.
        </p>
        <table className="pr-table">
          <tbody>
            {expenses.byGroup.map((g) => (
              <tr key={g.group}><th>{g.group}</th><td>{money(g.annual)}</td></tr>
            ))}
            <tr><th>Allowance for unlisted costs</th><td>{money(expenses.buffer)}</td></tr>
            <tr className="pr-strong"><th>Total per year</th><td>{money(expenses.annualTotal)} ({money(expenses.monthlyTotal)} a month)</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>3. USD → INR transfer{repatIsExample ? " (example figures)" : ""}</h2>
        <p className="pr-note">
          Sold {repat.timing === "before-move" ? "before moving" : "after moving"} in {repat.saleYear}, Indian status{" "}
          {repat.indiaStatus.toUpperCase()}, at ₹{repat.fxRate.toFixed(2)} per dollar.
        </p>
        <table className="pr-table">
          <tbody>
            <tr><th>Gross US savings</th><td>{usd(repatResult.grossUsd)}</td></tr>
            <tr><th>Forfeited and selling costs</th><td>−{usd(repatResult.lostUsd)}</td></tr>
            <tr><th>US tax</th><td>−{usd(repatResult.usTaxUsd)}</td></tr>
            <tr><th>US penalties</th><td>−{usd(repatResult.penaltyUsd)}</td></tr>
            <tr><th>FX spread and wire fees</th><td>−{usd(repatResult.feesUsd)}</td></tr>
            <tr><th>Indian tax</th><td>−{money(repatResult.indiaTaxInr)}</td></tr>
            <tr className="pr-strong"><th>Lands in India</th><td>{crore(repatResult.netInr, 2)}</td></tr>
          </tbody>
        </table>
        <h3>When you sell</h3>
        <table className="pr-table">
          <tbody>
            {timings.map((t) => (
              <tr key={t.id}><th>{t.label}</th><td>{crore(t.result.netInr, 2)} lands in India</td></tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>4. Other savings (today's value, before tax)</h2>
        <table className="pr-table">
          <tbody>
            <tr><th>Indian mutual funds and shares</th><td>{money(otherSavings.indianMarket)}</td></tr>
            <tr><th>Fixed deposits, bonds and bank balances</th><td>{money(otherSavings.deposits)}</td></tr>
            <tr><th>PPF and EPF</th><td>{money(otherSavings.providentFunds)}</td></tr>
            <tr><th>NPS{npsCounted ? "" : ` (not counted: locked until ${NPS_UNLOCK_AGE})`}</th><td>{money(otherSavings.nps)}</td></tr>
            <tr><th>Savings in other countries</th><td>{money(otherSavings.abroad)}</td></tr>
            <tr><th>Anything else</th><td>{money(otherSavings.other)}</td></tr>
            <tr className="pr-strong"><th>Counted towards the corpus</th><td>{crore(otherCounted, 2)}</td></tr>
          </tbody>
        </table>
        <p className="pr-note">Indian gains are taxed when you sell, so what you can spend is a little lower.</p>
      </section>

      <footer className="pr-foot">
        Your figures stay in your browser; this page was produced on your device. For estimation only —
        consult a professional licensed in both the US and India before acting.
      </footer>
    </div>
  );
}
